import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { AppConfig } from '../../utils/app-config.service';
import { Contract, ContractData, ContractLite, CreatedContract, Id, OrderClient, SelectedProduct } from '../../domain/data';
import {
    ContractDataInterface,
    ContractInterface,
    ContractLiteInterface,
    CreatedContractInterface,
    IdInterface,
    ProductInterface,
} from '../../../core';
import { LocalStorage } from '../helpers/local-storage';
import { MyHttpClient } from './my-http-client';
import { Serializer } from '../helpers/serializer/serializer';

import { mapCommonResponse } from './api-client.helpers';

import { environment } from '../../../../environments/environment';

@Injectable()
export class ContractApiClient {
    constructor(private http: MyHttpClient, private localStorage: LocalStorage, private serializer: Serializer) {}

    createContract(contract: ContractInterface): Observable<ContractInterface> {
        return of(AppConfig.settings.services.base + environment.services.paths.set_contract).pipe(
            switchMap((url) =>
                this.http.post(url, this.serializer.toJson(contract)).pipe(
                    map((resp: any) => {
                        if (resp || resp.Contract) {
                            return this.serializer.fromJson(resp.Contract, Contract);
                        }

                        throw new Error('error.general');
                    }),
                    map((resp: Contract) => {

                        if (contract.isAdvisoryModelSuitability() && contract.oldEncryptedCode) {
                            this.copyContractDocuments(contract.oldEncryptedCode, resp.encryptedCode).subscribe();
                        }

                        this.localStorage.contractCode = String(resp.encryptedCode);

                        if (resp.holders.length > 0) {
                            this.localStorage.clientCode = String(resp.holders[0].encryptedCode);
                            this.localStorage.clientId = String(resp.holders[0].id);
                        }

                        return resp;
                    })
                )
            )
        );
    }

    private copyContractDocuments(from: IdInterface, to: IdInterface): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.set_contract_copy_documents).pipe(
            switchMap((url) =>
                this.http
                    .post(url, {
                        ContractCodeOrigin: String(from),
                        ContractCodeDestination: String(to),
                    })
                    .pipe(map((resp: any) => mapCommonResponse(resp)))
            )
        );
    }

    createContractWithProducts(
        contract: ContractInterface,
        products: ProductInterface[],
        captcha: string
    ): Observable<CreatedContractInterface> {
        return of(AppConfig.settings.services.base + environment.services.paths.set_contract_with_products).pipe(
            switchMap((url) => {
                const selectedProducts: SelectedProduct[] = [];

                products.map((product) => {
                    const selectedProduct = new SelectedProduct();
                    selectedProduct.productId = product.id as Id;
                    selectedProduct.code = product.code as Id;
                    selectedProduct.encryptedCode = product.encryptedCode as Id;
                    selectedProduct.name = product.name;
                    selectedProduct.isin = product.isin;

                    selectedProducts.push(selectedProduct);
                });

                return this.http
                    .post(
                        url,
                        {
                            OnboardingContract: this.serializer.toJson(contract),
                            Products: this.serializer.toJsonArray(selectedProducts),
                        },
                        { headers: { 'Tower30-CaptchaToken': captcha } }
                    )
                    .pipe(
                        map((resp: any) => {
                            if (
                                !resp ||
                                (resp.Status !== 'OK' &&
                                    resp.Status !== 'ok' &&
                                    resp.Status !== 'NOOK' &&
                                    resp.Status !== 'nook' &&
                                    resp.Status !== '')
                            ) {
                                return new CreatedContract(false, resp.RedirectToPortal);
                            } else {
                                return new CreatedContract(true, resp.RedirectToPortal);
                            }
                        }),
                        catchError((err) => of(new CreatedContract(false, false)))
                    );
            })
        );
    }

    finalizeContract(contractCode: IdInterface): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.finalize_contract).pipe(
            switchMap((url) => this.http.post(url, { Code: String(contractCode) }).pipe(map((resp: any) => mapCommonResponse(resp))))
        );
    }

    cancelContract(contractCode: IdInterface): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.cancel_contract).pipe(
            switchMap((url) => this.http.post(url, { Code: String(contractCode) }).pipe(map((resp: any) => mapCommonResponse(resp))))
        );
    }

    getContract(): Observable<ContractInterface> {
        return of(AppConfig.settings.services.base + environment.services.paths.get_contract).pipe(
            switchMap((url) => {
                if (this.localStorage.contractCode) {
                    return this.http
                        .post(url, {
                            ContractCode: this.localStorage.contractCode,
                        })
                        .pipe(
                            map((resp: any) => {
                                if (resp && resp.Contract && resp.Contract.Code) {
                                    return this.serializer.fromJson(resp.Contract, Contract);
                                }

                                throw new Error('error.general');
                            })
                        );
                } else {
                    const contract = new Contract();
                    if (this.localStorage.branchCode) {
                        contract.branchCode = this.localStorage.branchCode;
                    }
                    return of(contract);
                }
            })
        );
    }

    getContracts(clientCode: IdInterface): Observable<ContractLiteInterface[]> {
        return of(AppConfig.settings.services.base + environment.services.paths.get_contracts).pipe(
            switchMap((url) =>
                this.http
                    .post(url, {
                        Id: String(clientCode),
                    })
                    .pipe(map((resp: any) => this.serializer.fromJsonArray(resp, ContractLite)))
            )
        );
    }

    getContractData(contractCode: IdInterface): Observable<ContractDataInterface> {
        return of(AppConfig.settings.services.base + environment.services.paths.get_contract_data).pipe(
            switchMap((url) =>
                this.http
                    .post(url, {
                        PortfolioCode: String(contractCode),
                    })
                    .pipe(map((resp: any) => this.serializer.fromJson(resp, ContractData)))
            )
        );
    }

    getContractSignatures(contractCode: IdInterface, clientCode?: IdInterface): Observable<any> {
        if (!!clientCode) {
            return of(AppConfig.settings.services.base + environment.services.paths.get_contract_action_signatories).pipe(
                switchMap((url) =>
                    this.http
                        .post(url, {
                            ContractCode: String(contractCode),
                            ClientCode: String(clientCode),
                        })
                        .pipe(map((resp: any) => this.serializer.fromJsonArray(resp.OrderClients || [], OrderClient)))
                )
            );
        }

        return of(AppConfig.settings.services.base + environment.services.paths.get_contract_signatories).pipe(
            switchMap((url) =>
                this.http
                    .post(url, {
                        ContractCode: String(contractCode),
                    })
                    .pipe(map((resp: any) => this.serializer.fromJsonArray(resp.OrderClients || [], OrderClient)))
            )
        );
    }

    rejectContract(contractCode: IdInterface, message: string): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.reject_contract).pipe(
            switchMap((url) =>
                this.http
                    .post(url, {
                        Code: String(contractCode),
                        Message: message,
                    })
                    .pipe(map((resp: any) => mapCommonResponse(resp)))
            )
        );
    }

    reviseContract(contractCode: IdInterface, message: string): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.unfinalize_contract).pipe(
            switchMap((url) =>
                this.http
                    .post(url, {
                        Code: String(contractCode),
                        Message: message,
                    })
                    .pipe(map((resp: any) => mapCommonResponse(resp)))
            )
        );
    }

    updateContract(contract: ContractInterface): Observable<ContractInterface> {
        return this.createContract(contract);
    }

    validateContract(contractCode: IdInterface): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.validate_contract).pipe(
            switchMap((url) => this.http.post(url, { Code: String(contractCode) }).pipe(map((resp: any) => mapCommonResponse(resp))))
        );
    }

    validateNotification(contractCode: IdInterface): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.validate_notification).pipe(
            switchMap((url) =>
                this.http.post(url, { ContractCode: String(contractCode) }).pipe(map((resp: any) => mapCommonResponse(resp)))
            )
        );
    }
}
